import { useState, useEffect, FC, FormEvent, ChangeEvent } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  CONTACT_PERSON,
  BUSINESS_ID,
  ADDRESS,
  SOCIAL_LINKS,
  DELIVERY_AREAS,
} from '../constants/contact';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  location: string;
  message: string;
}

const ContactSection = styled.section`
  background-color: ${theme.white};
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    padding: 5rem 2rem;
  }
`;

const ContactInfo = styled.div`
  h2 {
    color: ${theme.primaryDark};
    margin-bottom: 1.5rem;
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
  
  .contact-item {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    
    .icon {
      font-size: 1.5rem;
      margin-right: 1rem;
      color: ${theme.primary};
    }
  }
  
  .social-links {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    
    a {
      font-size: 1.5rem;
      color: ${theme.primaryDark};
      transition: color 0.3s ease;
      
      &:hover {
        color: ${theme.primary};
      }
    }
  }
`;

const ContactForm = styled.div`
  background-color: ${theme.secondary};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h3 {
    color: ${theme.primaryDark};
    margin-bottom: 1.5rem;
  }
  
  form {
    display: flex;
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid ${theme.borderMedium};
    border-radius: 4px;
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: ${theme.primary};
    }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background-color: ${theme.primaryDark};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;
  
  &:hover {
    background-color: ${theme.primary};
  }
  
  &:disabled {
    background-color: ${theme.disabled};
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: ${theme.success};
  color: ${theme.successText};
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const DeliveryArea = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.borderLight};
  
  h3 {
    color: ${theme.primaryDark};
    margin-bottom: 1rem;
  }
  
  ul {
    columns: 2;
    list-style-type: none;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      columns: 1;
    }
    
    li {
      margin-bottom: 0.5rem;
      
      &:before {
        content: "✓ ";
        color: ${theme.primary};
      }
    }
  }
`;

const Contact: FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(() => setIsSubmitted(false), 5000);
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const emailBody = `
Nimi: ${formData.name}
Sähköposti: ${formData.email}
Puhelinnumero: ${formData.phone || 'Ei annettu'}
Paikkakunta: ${formData.location}

Viesti:
${formData.message}
    `;

    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`[MURSKE] Tarjouspyyntö - ${formData.name}`)}&body=${encodeURIComponent(emailBody)}`;

    window.location.href = mailtoLink;
    setIsSubmitted(true);
    setFormData({ name: '', email: '', phone: '', location: '', message: '' });
  };
  
  return (
    <ContactSection id="yhteystiedot">
      <ContactContent>
        <ContactInfo>
          <h2>Ota yhteyttä murskeasioissa</h2>
          <p>
            Haluatko lisätietoja CE-merkitystä tiilenpunaisen graniitin murskeesta tai tarvitsetko tarjouksen 
            toimituksesta rakennuskohteeseesi? Ota yhteyttä ja kerro tarpeistasi - autamme valitsemaan 
            oikean raekoon teknisiin vaatimuksiisi.
          </p>
          
          <div className="contact-item">
            <div className="icon">📍</div>
            <div>
              <p>Ruhalan Murske</p>
              <p>{ADDRESS.street}</p>
              <p>{ADDRESS.postal}</p>
              <p>Y-tunnus: {BUSINESS_ID}</p>
            </div>
          </div>
          
          <div className="contact-item">
            <div className="icon">📞</div>
            <div>
              <p>Puh: <a href={CONTACT_PHONE_HREF}>{CONTACT_PHONE}</a></p>
              <p>Yhteyshenkilö: {CONTACT_PERSON}</p>
            </div>
          </div>
          
          <div className="contact-item">
            <div className="icon">✉️</div>
            <div>
              <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
              <p><a href="https://ruhalanmurske.fi">ruhalanmurske.fi</a></p>
            </div>
          </div>
          
          <DeliveryArea>
            <h3>Toimitusalueemme Pirkanmaalla</h3>
            <ul>
              {DELIVERY_AREAS.map(area => (
                <li key={area}>{area}</li>
              ))}
            </ul>
          </DeliveryArea>
          
          <div className="social-links">
            <a href={SOCIAL_LINKS.facebook} aria-label="Facebook" target="_blank" rel="noopener noreferrer">FB</a>
          </div>


        </ContactInfo>
        
        <ContactForm>
          <h3>Pyydä tarjous murskeesta nyt!</h3>
          
          {isSubmitted && (
            <SuccessMessage>Sähköpostiohjelma on avattu! Jos mitään ei tapahtunut, tarkista selaimesi asetukset tai lähetä sähköposti manuaalisesti osoitteeseen {CONTACT_EMAIL}</SuccessMessage>
          )}
          
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="name">Nimi *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="email">Sähköposti *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="phone">Puhelinnumero</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="location">Paikkakunta Pirkanmaalla *</label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              >
                <option value="">Valitse paikkakunta</option>
                {DELIVERY_AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
                <option value="Muu">Muu Pirkanmaan alue</option>
              </select>
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="message">Viesti - kerro murskeen käyttökohteesta *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Kerro käyttökohteesta (esim. tien kantava kerros, salaojitus, betonin kiviaines) ja kuinka paljon mursketta tarvitset. Mainitse myös mahdolliset tekniset vaatimukset ja raekoon tarve. Autamme valitsemaan oikean murskekoon."
                required
              ></textarea>
            </FormGroup>
            
            <SubmitButton type="submit">Lähetä tarjouspyyntö</SubmitButton>
          </form>
        </ContactForm>
      </ContactContent>
    </ContactSection>
  );
};

export default Contact; 